(() => {
  const HOLDER_SELECTOR = '[id="CartDrawerRecommendations"]';
  const LIMIT           = 12;
  const RECS_URL        = '{{ routes.product_recommendations_url }}';

  const CHECK_INTERVAL  = 2000;   // poll drawer open/close
  const RERENDER_DELAY  = 2500;    // debounce cart bursts
  const FADE_TIME       = 2500;    // ms for the cross-fade

  let drawerWasOpen        = false;
  let lastRenderedProductId = null;
  let rerenderTimer         = null;

  /* ---------- small helpers ---------- */

  const $ = (s, ctx = document) => Array.from(ctx.querySelectorAll(s));

  const buildRecUrl = pid =>
    `${RECS_URL}?product_id=${pid}&limit=${LIMIT}&intent=related&section_id=product-recommendations`;

  const formatPrice = p =>
    (Number(p) > 1000 ? p / 100 : Number(p))
      .toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  const productCard = p => {
    const url = `/products/${p.handle}`;
    const img = p.images?.[0]?.src
      ? p.images[0].src.replace(/(\.[a-z]+)(\?.*)?$/, '_400x$1')
      : '//cdn.shopify.com/s/assets/no-image-400x.gif';
    return `
      <div class="grid__item grid-product small--one-half medium-up--one-quarter">
        <a href="${url}" class="grid-product__link">
          <div class="image-wrap">
            <img src="${img}" alt="${p.title}" width="400" height="400" loading="lazy" decoding="async">
          </div>
          <div class="grid-product__meta">
            <div class="grid-product__title">${p.title}</div>
            <div class="grid-product__price">${formatPrice(p.variants[0].price)}</div>
          </div>
        </a>
      </div>`;
  };

  /* preload all images in a markup string */
  const preloadImages = html => {
    const tmp  = document.createElement('div');
    tmp.innerHTML = html;
    const imgs = $('img', tmp);

    return Promise.all(
      imgs.map(img => {
        // Already in cache? decode() resolves instantly
        return img.decode ? img.decode().catch(() => {}) : Promise.resolve();
      })
    ).then(() => tmp.innerHTML);
  };

  const fetchFallback = async handle => {
    try {
      const j = await fetch(`/collections/${handle}/products.json?limit=${LIMIT}`).then(r => r.json());
      return (j.products || []).map(productCard).join('');
    } catch (e) {
      console.error('fallback fetch error', e);
      return '';
    }
  };

  const makeBlock = (title, bodyHtml) => `
    <div class="header-part">${title}</div>
    <div class="product-part" data-event="open">${bodyHtml}</div>
  `;

  /* ---------- render one holder ---------- */

  const renderHolder = async (holder, pid) => {
    const fallbackColl = holder.dataset.fallbackCollection || 'ymi-best-sellers';
    let   recUrl       = holder.dataset.url || (pid ? buildRecUrl(pid) : null);

    let htmlBody   = '';
    let blockTitle = '';

    try {
      if (recUrl) {
        const outer = await fetch(recUrl).then(r => r.text());
        const tmp   = document.createElement('div');
        tmp.innerHTML = outer;

        const recs = tmp.querySelector('.product-recommendations');
        if (recs && recs.innerHTML.trim()) {
          htmlBody   = recs.innerHTML;
          blockTitle = 'Frequently Bought Together';
        }
      }
      if (!htmlBody) {
        htmlBody   = await fetchFallback(fallbackColl);
        blockTitle = 'Best Sellers';
      }
    } catch (err) {
      console.error(err);
      htmlBody   = await fetchFallback(fallbackColl);
      blockTitle = 'Best Sellers';
    }

    const incomingMarkup = makeBlock(blockTitle, htmlBody);

    /* skip if completely identical to what’s already showing */
    if (incomingMarkup === holder.dataset.renderedHtml) return;

    /* ---------- no-flash swap ---------- */

    try {
      const hydratedHTML = await preloadImages(incomingMarkup);

      const stage = document.createElement('div');
      stage.innerHTML = hydratedHTML;
      stage.style.opacity  = '0';
      stage.style.transition = `opacity ${FADE_TIME}ms`;

      /* keep current height to avoid layout shift */
      holder.style.minHeight = holder.offsetHeight + 'px';

      holder.appendChild(stage);          // new block hidden
      void stage.offsetWidth;             // force re-flow
      stage.style.opacity = '1';          // fade in

      setTimeout(() => {
        /* remove old siblings except the freshly added one */
        $(':scope > *', holder)
          .filter(el => el !== stage)
          .forEach(el => el.remove());

        holder.style.minHeight = '';      // reset
        holder.dataset.renderedHtml = hydratedHTML;

        if (typeof theme?.initQuickShop === 'function') theme.initQuickShop();
      }, FADE_TIME);
    } catch (e) {
      console.error('swap error', e);
    }
  };

  /* ---------- render all holders ---------- */

  const renderAll = async () => {
    const holders = $(HOLDER_SELECTOR);
    if (!holders.length) return;

    let firstId = null;
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      firstId    = cart.items?.[0]?.product_id || null;
    } catch (e) {
      console.error('cart fetch error', e);
    }

    if (firstId && firstId === lastRenderedProductId) return;
    lastRenderedProductId = firstId;

    holders.forEach(h => renderHolder(h, firstId));
  };

  /* ---------- scheduling helpers ---------- */

  const queueRender = () => {
    clearTimeout(rerenderTimer);
    rerenderTimer = setTimeout(renderAll, RERENDER_DELAY);
  };

  /* ---------- observers & hooks ---------- */

  // watch drawer open / close
  setInterval(() => {
    const isOpen = document.body.classList.contains('drawer--is-open');
    if (isOpen && !drawerWasOpen) queueRender();
    drawerWasOpen = isOpen;
  }, CHECK_INTERVAL);

  // intercept cart-affecting fetches
  const nativeFetch = window.fetch;
  window.fetch = async (...args) => {
    const res = await nativeFetch.apply(this, args);
    try {
      const url = typeof args[0] === 'string' ? args[0] : args[0].url;
      if (/\/cart\/(add|change|update)(\.js)?/i.test(url)) {
        lastRenderedProductId = null;
        queueRender();
      }
    } catch { /* ignore */ }
    return res;
  };

  // first paint
  document.addEventListener('DOMContentLoaded', queueRender);
})();